<template>
  <div class="card shadow-sm rounded-3 h-100 border-0">
    <div class="card-body p-10">
      <h4 class="card-title text-center text-primary h6 mb-10">{{ subCategoryName }}</h4>

      <template v-if="!hideTable">
        <div class="mb-20">
          <div class="table-responsive px-20">
            <table class="table table-sm m-0 custom-table">
              <thead class="table-light">
              <tr>
                <th></th>
                <template v-if="selectedPopulation === 'true'">
                  <th
                      v-for="popId in populationGroups"
                      :key="popId"
                      class="fw-bolder fs-6 text-center"
                      :title="surveyReport[popId]?.name || popId"
                      style="font-size: 0.7rem; padding: 0.3rem;"
                  >
                    {{ surveyReport[popId]?.acronym || popId }}
                  </th>
                </template>
                <template v-else>
                  <th class="fw-bolder text-center">
                    {{ $t('qvct_survey.survey_report.all_population') }}
                  </th>
                </template>
              </tr>
              </thead>
              <tbody>
              <tr v-for="terminalCat in terminalCategories" :key="terminalCat.name">
                <td class="fw-bold fs-6">{{ terminalCat.name }}</td>
                <template v-if="selectedPopulation === 'true'">
                  <td
                      v-for="popId in populationGroups"
                      :key="popId"
                      class="text-center align-middle"
                      :style="(() => {
            const data = getTerminalCategoryData(popId, terminalCat.name)
            const color = data?.average_percent > 0 ? data.color : 'transparent'
            return {
              backgroundColor: color,
              color: data?.average_percent > 0 ? '#ffffff' : '#000000',
              fontSize:'12px',
              border: `1px solid ${color}`,
              width: '60px'
            }
          })()"
                  >
        <span
            v-if="getTerminalCategoryData(popId, terminalCat.name) && getTerminalCategoryData(popId, terminalCat.name).average_percent > 0"
            class="badge d-block mx-auto"
        >

        </span>
                    <span v-else class="text-muted"></span>
                  </td>
                </template>
                <template v-else>
                  <td
                      class="text-center align-middle"
                      :style="(() => {
            const data = getTerminalCategoryData('all_populations', terminalCat.name)
            const color = data?.average_percent > 0 ? data.color : 'transparent'
            return {
              backgroundColor: color,
              color: data?.average_percent > 0 ? '#ffffff' : '#000000',
              fontSize:'12px',
              border: `1px solid ${color}`,
              width: '60px'
            }
          })()"
                  >
        <span
            v-if="getTerminalCategoryData('all_populations', terminalCat.name) && getTerminalCategoryData('all_populations', terminalCat.name).average_percent > 0"
            class="badge d-block mx-auto"
        >

        </span>
                    <span v-else class="text-muted"></span>
                  </td>
                </template>
              </tr>

              </tbody>
            </table>
          </div>
        </div>
      </template>

      <div class="chart-wrapper mt-5" style="height: 450px;">
        <canvas :id="chartId" :key="chartKey"></canvas>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import Chart from 'chart.js/auto'

const props = defineProps({
  surveyReport: { type: Object, required: true },
  selectedCategory: { type: String, required: true },
  selectedPopulation: { type: String, required: true },
  subCategoryName: { type: String, required: true },
  subCategoryPath: { type: Array, required: false, default: () => [] },
  hideTable: { type: Boolean, default: false },
})

const rawPathKey = computed(() => (props.subCategoryPath && props.subCategoryPath.length) ? props.subCategoryPath.join('-') : props.subCategoryName)
const safePathKey = computed(() => String(rawPathKey.value).replace(/[^a-zA-Z0-9_-]/g, '_'))
const chartId = computed(() => `radar-${props.selectedCategory}-${safePathKey.value}`)
const chartKey = computed(() => `radar-${props.selectedCategory}-${rawPathKey.value}-${props.selectedPopulation}`)
const chart = ref(null)

const populationGroups = computed(() => Object.keys(props.surveyReport || {}).filter(k => k !== 'all_populations'))

const terminalCategories = computed(() => {
  if (!props.surveyReport || !props.selectedCategory || !props.subCategoryName) return []
  const resolveNextLevelNames = (root) => {
    // Traverse along provided subCategoryPath (or subCategoryName) and return children keys at that node.
    let node = root
    for (const segment of (props.subCategoryPath && props.subCategoryPath.length ? props.subCategoryPath : [props.subCategoryName])) {
      const candidate = node?.[segment]
      if (!candidate) return []
      // If the segment itself has sub_categories, descend
      node = candidate?.sub_categories
      if (!node) return []
    }
    return Object.keys(node || {})
  }

  let terminalCats = []
  if (props.selectedPopulation === 'true') {
    const firstPopId = populationGroups.value[0]
    const root = props.surveyReport[firstPopId]?.types?.[props.selectedCategory]?.sub_categories
    if (root) terminalCats = resolveNextLevelNames(root)
  } else {
    const aggregated = props.surveyReport.all_populations || props.surveyReport
    const root = aggregated?.types?.[props.selectedCategory]?.sub_categories
    if (root) terminalCats = resolveNextLevelNames(root)
  }

  const isVisible = (name) => {
    if (props.selectedPopulation === 'true') {
      return populationGroups.value.some(popId => {
        const data = getTerminalCategoryData(popId, name)
        return (data?.average_percent || 0) > 0
      })
    }
    const data = getTerminalCategoryData('all_populations', name)
    return (data?.average_percent || 0) > 0
  }

  return terminalCats.filter(isVisible).map(name => ({ name }))
})

const getTerminalCategoryData = (populationId, terminalCatName) => {
  if (!props.surveyReport || !props.selectedCategory || !props.subCategoryName) return null
  const resolvePath = (root) => {
    let node = root
    const path = (props.subCategoryPath && props.subCategoryPath.length ? props.subCategoryPath : [props.subCategoryName])
    for (const segment of path) {
      const candidate = node?.[segment]
      if (!candidate) return null
      node = candidate?.sub_categories
      if (!node) return null
    }
    // Try to directly access
    if (node?.[terminalCatName]) return node[terminalCatName]
    // Fallback: search recursively for a matching leaf by name at deeper levels
    const dfs = (n) => {
      const subs = n?.sub_categories || {}
      if (subs[terminalCatName]) return subs[terminalCatName]
      for (const key of Object.keys(subs)) {
        const found = dfs(subs[key])
        if (found) return found
      }
      return null
    }
    return dfs({ sub_categories: node })
  }
  if (populationId === 'all_populations') {
    const aggregated = props.surveyReport.all_populations || props.surveyReport
    const root = aggregated?.types?.[props.selectedCategory]?.sub_categories
    return resolvePath(root)
  } else {
    const root = props.surveyReport[populationId]?.types?.[props.selectedCategory]?.sub_categories
    return resolvePath(root)
  }
}

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  scales: { r: { angleLines: { display: true }, grid: { color: 'rgba(0,0,0,0.1)' }, suggestedMin: 0, suggestedMax: 100, ticks: { stepSize: 20, backdropColor: 'transparent' } } },
  elements: { line: { borderWidth: 3, fill: false }, point: { radius: 4, hoverRadius: 6 } },
  plugins: {
    legend: { display: true, position: 'right', labels: { usePointStyle: false, padding: 15, boxWidth: 40, color: '#000', boxHeight: 8, pointStyle: 'rectRounded', font: { size: 15 } } },
    filler: { propagate: false },
    tooltip: { callbacks: { label: function(context) { return `${context.dataset.label}: ${context.raw}` } } }
  }
}

const getChartData = () => {
  if (!props.surveyReport || !props.selectedCategory || !props.subCategoryName) return { labels: [], datasets: [] }
  const labels = terminalCategories.value.map(cat => cat.name)
  if (!labels.length) return { labels: [], datasets: [] }
  const datasets = []
  if (props.selectedPopulation === 'true') {
    populationGroups.value.forEach(popId => {
      const population = props.surveyReport[popId]
      if (!population?.types?.[props.selectedCategory]) return
      const data = labels.map(label => getTerminalCategoryData(popId, label)?.average_percent || 0)

      const color = population.color || '#36a2eb'
      datasets.push({ label: ` ${population.name} (${popId})`, data, backgroundColor: color, borderColor: color, borderWidth: 2, pointBackgroundColor: color, pointBorderColor: '#fff', pointHoverRadius: 6, fill: false })
    })
  } else {
    const data = labels.map(label => getTerminalCategoryData('all_populations', label)?.average_percent || 0)
    datasets.push({label: 'Toutes populations confondues', data, backgroundColor: '#36a2eb', borderColor: '#36a2eb', borderWidth: 2, pointBackgroundColor: '#36a2eb', pointBorderColor: '#fff', pointHoverRadius: 6, fill: false })
  }
  return { labels, datasets }
}

const destroyChart = () => {
  if (chart.value) { try { chart.value.destroy() } catch (e) {} chart.value = null }
  try {
    const canvas = typeof document !== 'undefined' ? document.getElementById(chartId.value) : null
    if (canvas) {
      try { const existing = Chart.getChart && Chart.getChart(canvas); if (existing) existing.destroy() } catch (e) {}
      try { const inst = Chart.instances; const list = Array.isArray(inst) ? inst : (inst ? Object.values(inst) : []); list.forEach(i => { if (i && i.canvas === canvas) { try { i.destroy() } catch (e) {} } }) } catch (e) {}
      try { Object.keys(canvas).forEach(k => { if (k.startsWith('__chart') || k.startsWith('_chart')) canvas[k] = null }) } catch (e) {}
    }
  } catch (e) {}
}

const initChart = () => {
  destroyChart()
  nextTick(() => {
    const canvas = document.getElementById(chartId.value)
    if (!canvas) return
    try { const existing = Chart.getChart && Chart.getChart(canvas); if (existing) existing.destroy() } catch (e) {}
    const data = getChartData()
    if (data.labels.length === 0 || (data.datasets || []).length === 0) return
    try { chart.value = new Chart(canvas.getContext('2d'), { type: 'radar', data, options: chartOptions }) } catch (error) { console.error('Error creating radar chart:', error) }
  })
}

watch([() => props.surveyReport, () => props.selectedCategory, () => props.selectedPopulation, () => props.subCategoryPath?.join('-')], () => { initChart() }, { immediate: true, deep: true })

onMounted(() => {})

onUnmounted(() => { destroyChart() })
</script>

<style scoped>
.chart-wrapper {
  position: relative;
  width: 100%;
}

.table-sm th,
.table-sm td {
  padding: 0.5rem;
  font-size: 0.875rem;
}

.badge {
  font-size: 0.75rem;
  font-weight: 500;
  padding: 0.25em 0.4em;
  line-height: 1;
}

.custom-table {
  border-collapse: separate;
  border-spacing: 0;
  border: 1px solid #000 !important;
  border-radius: 0.375rem;
}

.custom-table th,
.custom-table td {
  border: 1px solid #000 !important;
  padding: 0.3rem !important;
  text-align: center;
  vertical-align: middle;
}

.custom-table thead th {
  border-bottom: 1px solid #000 !important;
  background-color: #FFFFFF !important;
}

.table-responsive {
  display: block;
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  margin-bottom: 50px;
}

.custom-table tbody tr:last-child td {
  border-bottom: 1px solid #000 !important;
}
</style>
